// @givmo/app-admin — the donor disclosure on the portal's charge dialogs (CAM-X4 web).
//
// A company that gives (the Give dialog) or pays a match (the approve dialog) is the donor of that
// gift, so it is shown the registry-served company-payer body before it pays, exactly as a person is
// shown theirs (CAM-X3, D-CAM-26 ruling E). This file is the ONE presentation both dialogs render.
//
// THE PRESENTATION IS D-CAM-41 (Jonathan, 2026-09-25, on the first mock set, where the full text ran
// under the button and filled about 75% of the dialog): the dialog is built around a donation summary
// (`charge-summary.tsx`), and the disclosure sits under a "Terms of your donation" divider, directly
// above the confirm control, as the donate app's fine print (the same size, line height and weight,
// in `neutral-600` at 90%: 5.56:1 on the panel's fill, where `text-text-tertiary` measured 4.47:1 —
// cut 25 review F2). The text is the FULL registry body, never collapsed; the
// region shows its first lines and is bounded so it holds at most about a quarter of the dialog, the
// rest reachable by scrolling — and, since his second look, visibly scrollable: a drawn scrollbar and
// a bottom fade (see `ChargeDisclosureTerms`). No words cue the scroll (D-CAM-36). The lines render
// VERBATIM; nothing here edits registry copy.
//
// THE CHARGE WAITS FOR IT. A dialog arms its confirm control only once the body has resolved, and a
// failed resolve says so and offers a retry rather than letting the gift go out with no text
// (`DisclosureUnavailable`). The donate app degrades the other way (a failed resolve renders nothing
// and the donation proceeds with no echo); the portal blocks on purpose.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';
import { Button, Icon, testIdProp } from '@givmo/design-system';
import { AlertTriangle } from 'lucide-react';

/** A resolve that has not answered in this long is treated as failed (the retry is offered). */
export const DISCLOSURE_RESOLVE_TIMEOUT_MS = 15_000;

/** Where a charge dialog's disclosure stands. Only `ready` arms the charge. */
export type ChargeDisclosureState<T> =
  | { status: 'loading' }
  | { status: 'ready'; value: T }
  | { status: 'unavailable' };

/**
 * Resolve a charge dialog's disclosure whenever `requestKey` changes (a new gift, target or rail),
 * and again on `reload` (the retry after a failure, or the re-resolve after a `409
 * DISCLOSURE_STALE`). `requestKey === null` keeps it inert (the dialog is closed, or there is no
 * funding choice yet).
 *
 * `load` resolves to the value to show, or `null` when there is nothing to show; a rejection or no
 * answer within {@link DISCLOSURE_RESOLVE_TIMEOUT_MS} counts as `null`. The first terminal answer
 * wins: a resolve landing after a newer request, a reload or the timeout is discarded, so the value
 * a dialog shows, and echoes, never changes under an armed confirm except through a new request.
 */
export function useChargeDisclosure<T>(
  requestKey: string | null,
  load: () => Promise<T | null>,
): { state: ChargeDisclosureState<T>; reload: () => void } {
  const [state, setState] = useState<ChargeDisclosureState<T>>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const generation = useRef(0);
  // The latest `load`, so a caller's fresh closure never re-runs the resolve by itself — only a new
  // `requestKey` or a `reload` does.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  });

  useEffect(() => {
    const current = ++generation.current;
    setState({ status: 'loading' });
    if (requestKey === null) return;

    const settle = (next: ChargeDisclosureState<T>) => {
      if (generation.current !== current) return;
      generation.current += 1;
      clearTimeout(timer);
      setState(next);
    };
    const timer = setTimeout(
      () => settle({ status: 'unavailable' }),
      DISCLOSURE_RESOLVE_TIMEOUT_MS,
    );
    loadRef.current().then(
      (value) => settle(value === null ? { status: 'unavailable' } : { status: 'ready', value }),
      () => settle({ status: 'unavailable' }),
    );
    return () => {
      clearTimeout(timer);
      generation.current += 1;
    };
  }, [requestKey, attempt]);

  const reload = useCallback(() => setAttempt((count) => count + 1), []);
  return { state, reload };
}

/** One body to show: its lines, and — only when a dialog shows more than one — who it applies to. */
export interface ChargeDisclosureSection {
  /** A stable React key (the body's content hash). */
  key: string;
  /** "Matches to …", or `null` when this is the dialog's only body. */
  label: string | null;
  lines: readonly string[];
}

export interface ChargeDisclosureTermsProps {
  sections: readonly ChargeDisclosureSection[];
  /** The stable `data-testid` for the scroll region (absent when there is nothing to show). */
  testId: string;
}

/** The divider's words (D-CAM-41). App-owned, not registry copy. */
export const CHARGE_TERMS_HEADING = 'Terms of your donation';

/** More text lies below the region's visible box (one-pixel tolerance for fractional layout). */
function hasMoreBelow(scrollTop: number, clientHeight: number, scrollHeight: number): boolean {
  return scrollTop + clientHeight < scrollHeight - 1;
}

interface ScrollMetrics {
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
}

const NO_METRICS: ScrollMetrics = { scrollTop: 0, clientHeight: 0, scrollHeight: 0 };

/**
 * The region's scroll geometry, re-read on scroll and whenever the region or its content changes
 * size (a new body, a font landing, a width change), so the fade and the thumb always describe the
 * text actually on screen.
 */
function useScrollMetrics(ref: RefObject<HTMLDivElement | null>, content: unknown) {
  const [metrics, setMetrics] = useState<ScrollMetrics>(NO_METRICS);
  const measure = useCallback(() => {
    const element = ref.current;
    if (!element) return;
    const next = {
      scrollTop: element.scrollTop,
      clientHeight: element.clientHeight,
      scrollHeight: element.scrollHeight,
    };
    setMetrics((previous) =>
      previous.scrollTop === next.scrollTop &&
      previous.clientHeight === next.clientHeight &&
      previous.scrollHeight === next.scrollHeight
        ? previous
        : next,
    );
  }, [ref]);

  useLayoutEffect(() => {
    measure();
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    for (const child of Array.from(element.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [measure, ref, content]);

  return { metrics, measure };
}

/** The drawn scrollbar's inset from the region's top and bottom edges, and its thumb's floor. */
const TRACK_INSET_PX = 8;
const MIN_THUMB_PX = 20;

/** The panel's nominal height: its padding, five 17.4px lines of fine print and its border. */
const PANEL_MAX_PX = 99;

/**
 * The panel's bottom edge snaps to a whole line. At the nominal 99px it falls between the fifth and
 * sixth lines of one paragraph, but a body of several paragraphs (the company deposit's four items,
 * 6px apart) can lay a line across that edge, which would then show only the tops of its glyphs under
 * the fade — the defect the one-paragraph bodies were first fixed for. The box grows just enough to
 * hold that line whole, so its first view still ends on a line. A one-paragraph body is unchanged.
 */
function useLineSnappedMaxHeight(ref: RefObject<HTMLDivElement | null>, content: unknown): number {
  const [maxHeight, setMaxHeight] = useState(PANEL_MAX_PX);
  useLayoutEffect(() => {
    const region = ref.current;
    if (!region) return;
    const snap = () => {
      const border = region.offsetHeight - region.clientHeight;
      const edge = PANEL_MAX_PX - border;
      // Line positions from the padding box's top at scroll 0, so a scrolled panel measures the same.
      const origin = region.getBoundingClientRect().top + region.clientTop - region.scrollTop;
      let next = PANEL_MAX_PX;
      for (const paragraph of Array.from(region.querySelectorAll('p'))) {
        const lineHeight = parseFloat(getComputedStyle(paragraph).lineHeight);
        if (!Number.isFinite(lineHeight) || lineHeight <= 0) continue;
        const rect = paragraph.getBoundingClientRect();
        const lines = Math.round(rect.height / lineHeight);
        for (let line = 0; line < lines; line += 1) {
          const top = rect.top - origin + line * lineHeight;
          const bottom = top + lineHeight;
          if (top < edge - 0.5 && bottom > edge + 0.5) next = Math.ceil(bottom) + border;
        }
      }
      setMaxHeight(next);
    };
    snap();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(snap);
    observer.observe(region);
    return () => observer.disconnect();
  }, [ref, content]);
  return maxHeight;
}

/**
 * The resolved body (or bodies) under the "Terms of your donation" divider, in a tinted, rounded
 * panel bounded to five lines of fine print (99px: its padding, five 17.4px lines and its border, so
 * the edge falls between lines; a body of spaced paragraphs snaps the edge to a whole line, see
 * `useLineSnappedMaxHeight`), the rest by scrolling — with the divider, at most a quarter of any
 * charge dialog at first view (D-CAM-41).
 *
 * THE BOX MUST LOOK SCROLLABLE AT A GLANCE (Jonathan, 2026-09-25, on the second mock: "it just
 * looks like cut off text"). macOS and phones hide an idle native scrollbar, so the region hides it
 * everywhere and draws its own: a thin track and a thumb in the portal's border tokens, always
 * visible while there is more text than the box holds, sized to the share on screen and placed at
 * the scroll position (a pointer can drag it or click the track). A bottom fade into the panel's
 * fill sits over the last visible line while more text lies below and clears at the end, so the
 * last line then reads in full — the fade the iOS Add Funds mock carries (D-CAM-38). No words cue
 * the scroll (D-CAM-36), and nothing permanently covers any line: every byte is in the DOM and
 * reachable by scrolling.
 *
 * The region is a keyboard stop (`role="region"`, its label, `tabIndex`), so arrow and Page keys
 * scroll it, with the house focus pairing for keyboard focus (the ring plus a 2px solid brand
 * outline; the ring alone is 1.23:1 — cut 25 review F1). Renders nothing when there are no lines.
 */
export function ChargeDisclosureTerms({ sections, testId }: ChargeDisclosureTermsProps) {
  const shown = sections.filter((section) => section.lines.length > 0);
  const regionRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  // Re-measure when the bodies change, keyed by their hashes (the array itself is rebuilt each render).
  const contentKey = shown.map((section) => section.key).join('|');
  const { metrics, measure } = useScrollMetrics(regionRef, contentKey);
  const maxHeight = useLineSnappedMaxHeight(regionRef, contentKey);
  const drag = useRef<{ pointerId: number; startY: number; startScrollTop: number } | null>(null);

  const { scrollTop, clientHeight, scrollHeight } = metrics;
  const overflowing = scrollHeight - clientHeight > 1;
  const moreBelow = overflowing && hasMoreBelow(scrollTop, clientHeight, scrollHeight);
  const trackHeight = Math.max(0, clientHeight - 2 * TRACK_INSET_PX);
  const thumbHeight = overflowing
    ? Math.min(trackHeight, Math.max(MIN_THUMB_PX, (trackHeight * clientHeight) / scrollHeight))
    : 0;
  const maxScroll = Math.max(1, scrollHeight - clientHeight);
  const thumbTop = overflowing ? ((trackHeight - thumbHeight) * scrollTop) / maxScroll : 0;

  // Dragging the thumb scrolls the region by the same share of its travel; a click on the track
  // (outside the thumb) centres the thumb there.
  const scrollPerPixel = trackHeight > thumbHeight ? maxScroll / (trackHeight - thumbHeight) : 0;
  const onThumbPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const region = regionRef.current;
    if (!region) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    drag.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startScrollTop: region.scrollTop,
    };
  };
  const onThumbPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const region = regionRef.current;
    const state = drag.current;
    if (!region || state === null || state.pointerId !== event.pointerId) return;
    region.scrollTop = state.startScrollTop + (event.clientY - state.startY) * scrollPerPixel;
    measure();
  };
  const onThumbPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId === event.pointerId) drag.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };
  const onTrackPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const region = regionRef.current;
    const track = trackRef.current;
    if (!region || !track) return;
    const offset = event.clientY - track.getBoundingClientRect().top - thumbHeight / 2;
    region.scrollTop = offset * scrollPerPixel;
    measure();
  };

  if (shown.length === 0) return null;

  return (
    <div className="flex w-full flex-col gap-8 text-left">
      <div className="flex items-center gap-10" aria-hidden>
        <span className="font-sans text-s12 font-w600 leading-h15 tracking-normal text-text-secondary">
          {CHARGE_TERMS_HEADING}
        </span>
        <span className="h-px flex-1 bg-border-subtle" />
      </div>
      <div className="relative">
        <div
          ref={regionRef}
          className="max-h-[99px] space-y-12 overflow-y-auto rounded-lg border border-border-subtle bg-surface-sunken py-10 pr-20 pl-12 outline-none [scrollbar-width:none] focus-visible:shadow-ring focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 [&::-webkit-scrollbar]:hidden"
          style={{ maxHeight }}
          role="region"
          aria-label={CHARGE_TERMS_HEADING}
          tabIndex={0}
          onScroll={measure}
          {...testIdProp(testId)}
        >
          {shown.map((section) => (
            // No gap under a label: it sits on the same 17.4px line grid as the text (Approve all).
            <div key={section.key} className="flex flex-col">
              {section.label !== null && (
                <p className="font-sans text-s12 font-w600 leading-[1.45] tracking-normal text-text-secondary">
                  {section.label}
                </p>
              )}
              {section.lines.map((line, index) => (
                <p
                  key={index}
                  className={`font-sans text-s12 font-w400 leading-[1.45] tracking-normal text-neutral-600/90 ${
                    index > 0 ? 'mt-6' : ''
                  }`}
                >
                  {line}
                </p>
              ))}
            </div>
          ))}
        </div>
        {/* The fade over the last visible line while more text lies below — into the panel's fill,
            inside its border, never over the drawn scrollbar. */}
        <div
          aria-hidden
          className={`pointer-events-none absolute right-20 bottom-px left-px h-28 rounded-bl-lg bg-gradient-to-t from-surface-sunken from-15% to-transparent transition-opacity duration-150 ${
            moreBelow ? 'opacity-100' : 'opacity-0'
          }`}
          data-visible={moreBelow ? 'true' : 'false'}
          {...testIdProp(`${testId}-fade`)}
        />
        {overflowing && (
          <div
            ref={trackRef}
            aria-hidden
            className="absolute right-6 w-4 rounded-full bg-border"
            style={{ top: TRACK_INSET_PX, height: trackHeight }}
            onPointerDown={onTrackPointerDown}
            {...testIdProp(`${testId}-track`)}
          >
            <div
              className="absolute inset-x-0 cursor-grab touch-none rounded-full bg-text-tertiary/45 active:cursor-grabbing"
              style={{ top: thumbTop, height: thumbHeight }}
              onPointerDown={onThumbPointerDown}
              onPointerMove={onThumbPointerMove}
              onPointerUp={onThumbPointerUp}
              onPointerCancel={onThumbPointerUp}
              {...testIdProp(`${testId}-thumb`)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export interface DisclosureUnavailableProps {
  /** Why the charge is held, and that nothing moved — the dialog's own sentence. */
  message: string;
  onRetry: () => void;
  testId: string;
}

/**
 * The resolve failed (a network error, a server error, or no body for this gift). The charge stays
 * disabled; this says why, that nothing moved, and offers the re-resolve.
 */
export function DisclosureUnavailable({ message, onRetry, testId }: DisclosureUnavailableProps) {
  return (
    <div className="flex flex-col items-start gap-4" {...testIdProp(testId)}>
      <div className="flex items-start gap-8">
        <Icon icon={AlertTriangle} size={16} aria-hidden className="mt-1 text-danger-700" />
        <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-danger-700">
          {message}
        </p>
      </div>
      <Button
        appearance="saas"
        tone="link"
        size="sm"
        className="ml-24"
        onClick={onRetry}
        testId={`${testId}-retry`}
      >
        Try again
      </Button>
    </div>
  );
}
